/* eslint-disable default-param-last */
/* eslint-disable class-methods-use-this */

import cloneDeep from 'lodash.clonedeep';
import setAuctionType from '@/api/SetAuctionType';
import store from '@/store';
import FILTERED_AUCTIONS_QUERY from '@/api/graphql/definitions/queries/filteredAuctionsQuery';
import COUNTS_QUERY from '@/api/graphql/definitions/queries/countsQuery';
import AUCTION_LIVE_UPDATES_SUBSCRIPTION from '@/api/graphql/definitions/subscriptions/auctionLiveUpdatesSubscription';
import GaiaGraphQLClient from '@/api/graphql/GaiaGraphQLClient';
import EasyPassClient from './EasyPassClient';
import SearchServiceClient from './SearchServiceClient';

class AuctionsClient {
  constructor(user) {
    this.easyPassClient = new EasyPassClient(user);
    this.searchServiceClient = new SearchServiceClient();
    this.rootUri = 'auctions';
    this.easyPassRootUri = 'api/acvapi/v3';
    this.customHeaders = {
      'json-naming-strategy': 'camel',
    };
    this.defaultPageSize = 12;
  }

  handleEasyPassResponse(response) {
    return {
      ...response.data.data,
    };
  }

  getData(
    filters,
    savedSearchIds,
    size = this.defaultPageSize,
    subFilterSelection = {},
    relatedRecommendations = undefined,
    includeCounts = undefined,
    privateLaneIds,
    includedLanes,
  ) {
    const [subFilterSelectionKey] = Object.keys(subFilterSelection);
    const data = {
      filters,
      savedSearchIds,
      size,
      [subFilterSelectionKey]: subFilterSelection[subFilterSelectionKey],
      relatedRecommendations,
      includeCounts,
      privateLaneIds,
      includedLanes,
    };
    return data;
  }

  getOutstandingAuctions(options = {}) {
    const params = cloneDeep(options);
    params.my_acv = true;
    return this.easyPassClient.get(`${this.easyPassRootUri}/${this.rootUri}/to_finalize`, params)
      .then((response) => setAuctionType(response, 'buying'));
  }

  getFiltersAuctionsBuyingActive(
    filters,
    savedSearchIds,
    size,
    subFilterSelection,
    relatedRecommendations = undefined,
    cancelToken,
    retries = 6,
    sort = undefined,
  ) {
    const data = this.getData(
      filters,
      savedSearchIds,
      size,
      subFilterSelection,
      relatedRecommendations,
      false,
    );
    if (sort) {
      data.sort = sort;
    }
    return this.easyPassClient.post(
      'bff/filters/auctions/buying/active',
      data,
      this.customHeaders,
      cancelToken,
      retries,
    )
      .then((response) => this.handleEasyPassResponse(response));
  }

  getFiltersAuctionsBuyingRunList(
    filters,
    savedSearchIds,
    size,
    subFilterSelection,
    relatedRecommendations = undefined,
    cancelToken,
    retries = 6,
  ) {
    return this.easyPassClient.post(
      'bff/filters/auctions/buying/run_list',
      this.getData(
        filters,
        savedSearchIds,
        size,
        subFilterSelection,
        relatedRecommendations,
        false,
      ),
      this.customHeaders,
      cancelToken,
      retries,
    )
      .then((response) => this.handleEasyPassResponse(response));
  }

  getFiltersAuctionsBuyingNoReservePreview(
    filters,
    savedSearchIds,
    size,
    subFilterSelection,
    relatedRecommendations = undefined,
    cancelToken,
    retries = 6,
  ) {
    return this.easyPassClient.post(
      'bff/filters/auctions/buying/no_reserve_preview',
      this.getData(
        filters,
        savedSearchIds,
        size,
        subFilterSelection,
        relatedRecommendations,
        false,
      ),
      this.customHeaders,
      cancelToken,
      retries,
    )
      .then((response) => this.handleEasyPassResponse(response));
  }

  getFiltersAuctionsBuyingPrivateLane(
    filters,
    savedSearchIds,
    size,
    subFilterSelection,
    relatedRecommendations = undefined,
    cancelToken,
    laneId,
    retries = 6,
  ) {
    return this.easyPassClient.post(
      `bff/filters/auctions/buying/private_lane/${laneId}`,
      this.getData(
        filters,
        savedSearchIds,
        size,
        subFilterSelection,
        relatedRecommendations,
        false,
      ),
      this.customHeaders,
      cancelToken,
      retries,
    )
      .then((response) => this.handleEasyPassResponse(response));
  }

  // eslint-disable-next-line max-len
  getFiltersAuctionsBuyingActiveRecommended(
    filters,
    savedSearchIds,
    size,
    subFilterSelection,
    cancelToken,
  ) {
    return this.easyPassClient.post(
      'bff/filters/auctions/buying/active/recommended',
      this.getData(filters, savedSearchIds, size, subFilterSelection, false),
      this.customHeaders,
      cancelToken,
    )
      .then((response) => this.handleEasyPassResponse(response));
  }

  // eslint-disable-next-line max-len
  getFiltersAuctionsBuyingRunListRecommended(
    filters,
    savedSearchIds,
    size,
    subFilterSelection,
    cancelToken,
  ) {
    return this.easyPassClient.post(
      'bff/filters/auctions/buying/run_list/recommended',
      this.getData(filters, savedSearchIds, size, subFilterSelection, false),
      this.customHeaders,
      cancelToken,
    )
      .then((response) => this.handleEasyPassResponse(response));
  }

  // eslint-disable-next-line max-len
  getFiltersAuctionsBuyingNoReservePreviewRecommended(
    filters,
    savedSearchIds,
    size,
    subFilterSelection,
    cancelToken,
  ) {
    return this.easyPassClient.post(
      'bff/filters/auctions/buying/no_reserve_preview/recommended',
      this.getData(filters, savedSearchIds, size, subFilterSelection, false),
      this.customHeaders,
      cancelToken,
    )
      .then((response) => this.handleEasyPassResponse(response));
  }

  // eslint-disable-next-line max-len
  getFiltersAuctionsBuyingMakeOfferRecommended(
    filters,
    savedSearchIds,
    size,
    subFilterSelection,
    cancelToken,
  ) {
    return this.easyPassClient.post(
      'bff/filters/auctions/buying/make_offer/recommended',
      this.getData(filters, savedSearchIds, size, subFilterSelection, false),
      this.customHeaders,
      cancelToken,
    )
      .then((response) => this.handleEasyPassResponse(response));
  }

  addToWatchList(savedAuctionId, cancelToken, useSearchService = false) {
    if (useSearchService) {
      return this.searchServiceClient.addWatchlist(savedAuctionId);
    }

    return this.easyPassClient.post(
      `query-engine/watchlist/saved_auction/${savedAuctionId}`,
      null,
      this.customHeaders,
      cancelToken,
      6,
    );
  }

  getWatchList() {
    return this.easyPassClient.get(
      'bff/filters/auctions/buying/watchlist',
      null,
      this.customHeaders,
    )
      .then((response) => this.handleEasyPassResponse(response));
  }

  removeFromWatchList(savedAuctionId, cancelToken, useSearchService = false) {
    if (useSearchService) {
      return this.searchServiceClient.removeWatchlist(savedAuctionId);
    }

    return this.easyPassClient.delete(
      `query-engine/watchlist/saved_auction/${savedAuctionId}`,
      null,
      this.customHeaders,
      null,
      cancelToken,
      6,
    );
  }

  getSavedSearches(useSearchServiceSavedSearches = false) {
    if (useSearchServiceSavedSearches) {
      return this.searchServiceClient.getSavedSearches();
    }
    return this.easyPassClient.get('query-engine/saved_searches', null, this.customHeaders);
  }

  addSavedSearch(savedSearch, useSearchServiceSavedSearches = false) {
    if (useSearchServiceSavedSearches) {
      return this.searchServiceClient.saveSavedSearch(savedSearch);
    }
    return this.easyPassClient.post('query-engine/saved_search', savedSearch, this.customHeaders);
  }

  getSavedSearch(savedSearchId, useSearchServiceSavedSearches = false) {
    if (useSearchServiceSavedSearches) {
      return this.searchServiceClient.getSavedSearch(savedSearchId);
    }
    return this.easyPassClient.get(`query-engine/saved_search/${savedSearchId}`, null, this.customHeaders);
  }

  updateSavedSearch(savedSearch, useSearchServiceSavedSearches = false) {
    if (useSearchServiceSavedSearches) {
      return this.searchServiceClient.updateSavedSearch(savedSearch);
    }
    return this.easyPassClient.put(`query-engine/saved_search/${savedSearch.id}`, savedSearch, this.customHeaders);
  }

  deleteSavedSearch(savedSearchId, useSearchServiceSavedSearches = false) {
    if (useSearchServiceSavedSearches) {
      return this.searchServiceClient.deleteSavedSearch(savedSearchId);
    }
    return this.easyPassClient.delete(`query-engine/saved_search/${savedSearchId}`, null, this.customHeaders);
  }

  getAuctionEndedCountMatchingFilter(filterData) {
    return this.searchServiceClient.getAuctionEndedCountsMatchingFilter(filterData);
  }

  getWonAuctionStats() {
    return this.easyPassClient.get('bff/auctions/buying/search/won/dashboard', null, this.customHeaders);
  }

  getMyNeedsFinalizing() {
    return this.easyPassClient.get(
      'bff/auctions/buying/search/myacv/needs_finalization',
      null,
      this.customHeaders,
    )
      .then((response) => this.handleEasyPassResponse(response));
  }

  getMyPendingProxies() {
    return this.easyPassClient.get(
      'bff/auctions/buying/search/myacv/pending_proxy',
      null,
      this.customHeaders,
    )
      .then((response) => this.handleEasyPassResponse(response));
  }

  getMyActiveAuctions() {
    return this.easyPassClient.get(
      'bff/auctions/buying/search/myacv/active',
      null,
      this.customHeaders,
    )
      .then((response) => this.handleEasyPassResponse(response));
  }

  getMyNegotiations() {
    return this.easyPassClient.get(
      'bff/auctions/buying/search/myacv/negotiation',
      null,
      this.customHeaders,
    )
      .then((response) => this.handleEasyPassResponse(response));
  }

  getAuctionsById(auctionIds, retries = 0) {
    return this.easyPassClient.post(
      'bff/auctions/buying/search/list_by_id',
      auctionIds,
      this.customHeaders,
      null,
      retries,
    )
      .then((response) => this.handleEasyPassResponse(response));
  }

  getMarketplaceLaneCounts(
    filters,
    savedSearchIds,
    size,
    subFilterSelection,
    relatedRecommendations = undefined,
    includedLanes = [],
    privateLaneIds = [],
    cancelToken,
  ) {
    return this.easyPassClient.post(
      'bff/filters/auctions/buying/counts',
      this.getData(
        filters,
        savedSearchIds,
        size,
        subFilterSelection,
        relatedRecommendations,
        undefined,
        privateLaneIds,
        includedLanes,
      ),
      this.customHeaders,
      cancelToken,
    )
      .then((response) => this.handleEasyPassResponse(response));
  }

  getFiltersAuctionsBuyingEnded(
    filters,
    savedSearchIds,
    size,
    subFilterSelection,
    relatedRecommendations = undefined,
    cancelToken,
    retries = 6,
    sort = undefined,
  ) {
    const data = this.getData(
      filters,
      savedSearchIds,
      size,
      subFilterSelection,
      relatedRecommendations,
      false,
    );
    if (sort) {
      data.sort = sort;
    }
    return this.easyPassClient.post(
      'bff/filters/auctions/buying/ended',
      data,
      this.customHeaders,
      cancelToken,
      retries,
    )
      .then((response) => this.handleEasyPassResponse(response));
  }

  getNoReservePreviewLaunchWindows() {
    return this.easyPassClient.get(`${this.easyPassRootUri}/auctions/preview/launch_windows`);
  }

  getCrossLanePromotionAuctions(
    filters,
    savedSearchIds,
    cancelToken,
    retries = 6,
  ) {
    return this.easyPassClient.post(
      'bff/filters/auctions/buying/cross_lane_promotion',
      {
        filters,
        savedSearchIds,
      },
      this.customHeaders,
      cancelToken,
      retries,
    )
      .then((response) => this.handleEasyPassResponse(response));
  }

  async getMarketplaceAuctions(params) {
    const paramsToUse = params;
    const { cancelToken } = paramsToUse;
    delete paramsToUse.cancelToken;

    const usingGraphQl = await store.dispatch(
      'getFlag',
      store.state.featureFlags.featureFlagSlugs.enableGraphqlOnSrp,
    );

    if (usingGraphQl) {
      const apiResponse = await GaiaGraphQLClient().query({
        operation: {
          query: FILTERED_AUCTIONS_QUERY,
          variables: {
            filters: paramsToUse.filters,
            savedSearchIds: paramsToUse.savedSearchIds,
            size: paramsToUse.size,
            sort: paramsToUse.sort,
          },
          context: {
            fetchOptions: {
              signal: cancelToken,
            },
          },
        },
      });

      return {
        results: apiResponse.data?.filteredAuctions,
      };
    }

    return this.easyPassClient
      .post(
        'bff/filters/auctions/buying/auctions',
        paramsToUse,
        this.customHeaders,
        cancelToken,
        paramsToUse.retries ?? 6,
      )
      .then((response) => this.handleEasyPassResponse(response));
  }

  async getMarketplaceCounts(params = {}) {
    const paramsToUse = params;
    const { cancelToken } = paramsToUse;
    delete paramsToUse.cancelToken;

    const usingGraphQl = await store.dispatch(
      'getFlag',
      store.state.featureFlags.featureFlagSlugs.enableGraphqlOnSrp,
    );

    if (usingGraphQl) {
      const apiResponse = await GaiaGraphQLClient().query({
        operation: {
          query: COUNTS_QUERY,
          variables: {
            filters: paramsToUse.filters,
            savedSearchIds: paramsToUse.savedSearchIds,
            privateLaneIds: paramsToUse.privateLaneIds,
          },
          context: {
            fetchOptions: {
              signal: cancelToken,
            },
          },
        },
      });

      return apiResponse.data?.counts;
    }

    return this.easyPassClient
      .post(
        'bff/filters/auctions/buying/counts_v2',
        paramsToUse,
        this.customHeaders,
        cancelToken,
      )
      .then((response) => this.handleEasyPassResponse(response));
  }

  async subscribeAuctionsByIds({
    auctionIds,
    callbackFn,
    handleSubscriptionFn,
  }) {
    const usingGraphQl = await store.dispatch(
      'getFlag',
      store.state.featureFlags.featureFlagSlugs.enableGraphqlOnSrp,
    );

    if (usingGraphQl) {
      GaiaGraphQLClient().subscribe({
        operation: {
          query: AUCTION_LIVE_UPDATES_SUBSCRIPTION,
          variables: {
            auction_ids: auctionIds,
          },
        },
        callbackFn,
        handleSubscriptionFn,
      });
    }
  }

  async unsubscribeAuctionSubscription({ subscriptionId }) {
    const usingGraphQl = await store.dispatch(
      'getFlag',
      store.state.featureFlags.featureFlagSlugs.enableGraphqlOnSrp,
    );

    if (usingGraphQl) {
      GaiaGraphQLClient().unsubscribe(subscriptionId);
    }
  }
}

export default AuctionsClient;
